import React from 'react'
import PropTypes from 'prop-types'
import PortalModal from 'components/Shared/PortalModal'
import { Trans, useTranslation } from 'react-i18next'

import { SecondaryButton } from 'components/Shared/Button'
import { useCampaignConfig, useCampaignConfigDispatch } from './CampaignConfigProvider'
import { numberWithThousandsSeparators } from 'utils/helpers'
import { RuntimeBudgetErrorCode } from 'domain/runtimeBudget/schemas'
import {
  SETTINGS_PLATFORMS_ANCHOR,
  permissionErrorProviderName,
} from 'domain/platform/permissionErrorTarget'

// Maps each documented RuntimeBudgetErrorCode to its own translation key
// (and, where the error payload carries data worth surfacing, the values for
// it) so every code gets a distinct, informative message instead of falling
// through to the generic permissionError/transientError copy.
const ERROR_CODE_MESSAGES = {
  [RuntimeBudgetErrorCode.noDates]: { key: 'noDates' },
  [RuntimeBudgetErrorCode.remoteCampaignNotFound]: { key: 'remoteCampaignNotFound' },
  [RuntimeBudgetErrorCode.platformAccountNotLinked]: { key: 'platformAccountNotLinked' },
  [RuntimeBudgetErrorCode.timezoneMismatch]: {
    key: 'timezoneMismatch',
    values: (error) => ({ fb: error.data?.fb, google: error.data?.google }),
  },
  [RuntimeBudgetErrorCode.campaignEnded]: { key: 'campaignEnded' },
  [RuntimeBudgetErrorCode.dailyBudgetTooLow]: {
    key: 'dailyBudgetTooLow',
    values: (error) => ({
      min: numberWithThousandsSeparators(error.data?.min),
      currency: error.data?.currency,
      // Meta and Google enforce different minimums; when the API names the
      // platform that refused, it wins over the generic provider fallback.
      ...(error.data?.platform
        ? { provider: permissionErrorProviderName(error.data.platform) }
        : {}),
    }),
  },
  [RuntimeBudgetErrorCode.endDateInvalid]: { key: 'endDateInvalid' },
  [RuntimeBudgetErrorCode.spendFetchFailed]: { key: 'spendFetchFailed' },
}

// Message keys whose copy contains {{provider}} (Meta / Google).
const PROVIDER_WORDED_MESSAGES = new Set([
  'permissionError',
  'transientError',
  'spendFetchFailed',
  'dailyBudgetTooLow',
])

export function ErrorContent({ isOpen, onClose, onRetry }) {
  const { error } = useCampaignConfig()
  const { hasPermissionError, getPermissionErrorPlatform } = useCampaignConfigDispatch()
  const { t } = useTranslation()

  if (!isOpen) {
    return null
  }

  const isPermissionError = hasPermissionError()
  const provider = permissionErrorProviderName(getPermissionErrorPlatform())
  const errorType = isPermissionError ? 'permissionError' : 'transientError'
  const codeMessage = error?.code ? ERROR_CODE_MESSAGES[error.code] : null
  const messageKey = codeMessage ? codeMessage.key : errorType
  const key = `Recommendations.campaignConfig.error.${messageKey}.description`
  const codeMessageValues = codeMessage?.values ? codeMessage.values(error) : {}
  const errorMsgData = {
    ...codeMessageValues,
    // Only the platform-worded messages take {{provider}}; adding it to the
    // others would just be an unused interpolation value. A code message that
    // names its own platform keeps it.
    ...(PROVIDER_WORDED_MESSAGES.has(messageKey) && !codeMessageValues.provider ? { provider } : {}),
  }

  return (
    <div className="yes-no-prompt_content cbr-error-content">
      <Trans
        i18nKey={key}
        components={{
          a: (
            <a
              href={SETTINGS_PLATFORMS_ANCHOR}
              rel="noopener noreferrer"
              target="_blank"
              className="d-inline-block"
              style={{ color: '#2F80ED' }}
            >
              asset page
            </a>
          ),
        }}
        values={errorMsgData}
      />
      {isPermissionError ? (
        <SecondaryButton
          linkTo={SETTINGS_PLATFORMS_ANCHOR}
          className="bt"
          color="orange"
          text={t('Recommendations.campaignConfig.error.permissionError.action', { provider })}
        />
      ) : onRetry ? (
        <SecondaryButton className="bt" text={t('common.tryAgain')} onClick={onRetry} color="orange" />
      ) : (
        <SecondaryButton className="bt" text={t('common.close')} onClick={onClose} color="orange" />
      )}
    </div>
  )
}

ErrorContent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onRetry: PropTypes.func,
}

export default function ErrorModal({ isOpen, onClose, onRetry }) {
  const { t } = useTranslation()
  const { error } = useCampaignConfig()
  const { hasPermissionError } = useCampaignConfigDispatch()

  const errorType = hasPermissionError() ? 'permissionError' : 'transientError'
  const codeMessage = error?.code ? ERROR_CODE_MESSAGES[error.code] : null
  const errorTitleKey = codeMessage
    ? `Recommendations.campaignConfig.error.${codeMessage.key}.title`
    : `Recommendations.campaignConfig.error.${errorType}.title`

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={onClose}
      title={t(errorTitleKey)}
      className="cbr-modal cbr-modal--error"
    >
      <div className="yes-no-prompt-prompt">
        <ErrorContent onClose={onClose} onRetry={onRetry} isOpen={isOpen} />
      </div>
    </PortalModal>
  )
}

ErrorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
}
